import { useAuth } from "../contexts/AuthContext";
import { criterionView, payoutRows, kpiSummary, checksAllNote, netNoteKey, type CriterionView, type CriterionLabels } from "../lib/retail";
import { criterionLabel } from "../lib/i18n";
import { fmt, fmt1, fmtNum, fmtPct1, fmtMonth } from "../lib/format";
import type { RetailRow } from "../lib/types";
import { TONE_BG } from "../lib/tone";



interface Props {
  row: RetailRow;
  /** владелец: видит пометки панели и цифры даже при data_pending */
  isAdmin: boolean;
  month: string;
  final: boolean;
}

/** Карточка сотрудницы за месяц (макет согласован 28.10.2026): KPI → критерии → смены и чеки → к выплате. */
export function EmployeeScreen({ row, isAdmin, month, final }: Props) {
  const { t, lang } = useAuth();
  const L: CriterionLabels = {
    labelOf: (key, fb) => criterionLabel(key, fb, lang),
    ofPct: t("crit_of"), planWord: t("crit_plan"), ofPlan: t("crit_of_plan"),
    points: t("crit_points"), baseAccrual: t("crit_base_accrual"), baseExtra: t("crit_base_extra"),
  };
  const pending = row.data_pending;

  if (pending && !isAdmin) {
    return (
      <section className="p-4">
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
          <div className="text-sm font-semibold text-amber-900">{t("data_pending_title")}</div>
          <div className="text-xs text-amber-800 mt-1">{t("data_pending_note")}</div>
        </div>
      </section>
    );
  }

  const summary = kpiSummary(row);
  const pay = payoutRows(row);
  const monthLabel = fmtMonth(month, lang).toLowerCase();
  const netNote = netNoteKey({ away: row.away_store != null, role: row.role }, final);

  return (
    <section className="p-4 space-y-3">
      {pending && isAdmin && (
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
          <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide">{t("problems_title")}</div>
          <ul className="text-xs text-amber-800 mt-1 list-disc pl-4">{(row.problems ?? []).map((p, i) => <li key={i}>{p}</li>)}</ul>
          <div className="text-[10px] text-amber-700 mt-1">{t("problems_note")}</div>
        </div>
      )}

      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-4 border border-teal-100">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-teal-700">{t("kpi_total_title")}</div>
          {!final && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{t("prelim_badge")}</span>}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-teal-900 tabular-nums">{fmt1(summary.kpi)}</span>
          <span className="text-sm text-teal-700">%</span>
        </div>
        <div className="text-xs text-slate-600 mt-1 tabular-nums">{t("kpi_pct_note", { pct: fmtPct1(summary.pct) })}</div>
        {summary.floorNote !== null && (
          <div className="text-[10px] text-slate-500 mt-1">{t("kpi_floor_note", { pct: fmtPct1(summary.floorNote) })}</div>
        )}
      </div>

      {row.criteria.map((c, i) => <CriterionCard key={`${c.key}-${i}`} v={criterionView(c, L)} />)}

      {/* Рабочие дни отдельным блоком: норма, запланировано, отработано (отработано дробное как есть) — просьба владельца 29.10.2026 */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">{t("days_title")}</div>
        <div className="grid grid-cols-3 gap-1.5 text-center">
          <div>
            <div className="text-base font-bold text-slate-900 tabular-nums">{fmtNum(row.norm_shifts)}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{t("days_norm")}</div>
          </div>
          <div>
            <div className="text-base font-bold text-slate-900 tabular-nums">{fmtNum(row.planned_shifts)}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{t("days_planned")}</div>
          </div>
          <div>
            <div className="text-base font-bold text-slate-900 tabular-nums">{fmtNum(row.worked_shifts)}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{t("days_worked")}</div>
          </div>
        </div>
        {row.sick_days > 0 && (
          <div className="text-[10px] text-slate-500 mt-2">{t("sick_note", { days: row.sick_days, paid: row.sick_paid })}</div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">{t("shifts_title")}</div>
        <div className="grid grid-cols-3 gap-1.5 text-center">
          <div>
            <div className="text-base font-bold text-slate-900 tabular-nums">{row.checks_count}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{t("checks_label")}</div>
          </div>
          <div>
            <div className="text-base font-bold text-slate-900 tabular-nums">{row.avg_check == null ? "—" : fmt(row.avg_check)}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{t("avg_check_label", { plan: fmt(row.avg_check_plan) })}</div>
          </div>
          <div>
            <div className="text-base font-bold text-slate-900 tabular-nums">{row.returns == null ? "—" : `−${fmt(row.returns)}`}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{t("returns_label")}</div>
          </div>
        </div>
        {/* вариант Б (решение владельца 28.10.2026): командировка выше с меткой «в KPI», справочная строка последней с меткой «не в KPI» */}
        {row.away_store && (row.away_checks_count ?? 0) > 0 && (
          <div className="text-[10px] text-slate-500 mt-2 tabular-nums">
            <KpiTag inKpi />
            {t("away_note", { store: row.away_store, n: row.away_checks_count ?? 0, avg: row.away_checks_avg == null ? "—" : fmt(row.away_checks_avg) })}
            {!final && ` ${t("away_pay_later")}`}
          </div>
        )}
        <ChecksAllLine parts={[row]} tagged />
      </div>

      <div className="bg-gradient-to-br from-teal-600 to-emerald-600 text-white rounded-2xl p-5 shadow-lg">
        <div className="text-xs opacity-80 uppercase tracking-wide">{t("payout_title")}</div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-4xl font-bold tabular-nums">{fmt(row.net)}</span>
          <span className="text-lg opacity-90">грн</span>
        </div>
        <div className="text-xs opacity-80 mt-1">
          {t(final ? "payout_sub_final" : "payout_sub", { store: row.store_label, month: monthLabel })}
        </div>
        {/* командировочные и админ-надбавка в предварительном месяце ещё не в сумме; та же пометка, что в списке магазина */}
        {netNote && <div className="text-xs mt-1 text-amber-100">{t(netNote)}</div>}
        <div className="mt-3 pt-3 border-t border-white/25 text-xs space-y-1">
          {pay.map(p => (
            <div key={p.key} className="flex justify-between">
              <span className="opacity-85">{t(`payout_${p.key}`)}</span>
              <span className="tabular-nums">{p.sign}{fmt(p.value)} грн</span>
            </div>
          ))}
          <div className="flex justify-between pt-3 mt-2 border-t border-white/25">
            <span className="opacity-85">{t("payout_net")}</span>
            <span className="font-semibold tabular-nums">{fmt(row.net)} грн</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Справочно: все чеки, включая до 1000 грн. Ничего не считает, только показывает. */
export function ChecksAllLine({ parts, tagged = false }: { parts: Array<{ checks_all_count: number | null; checks_all_sum: number | null; checks_all_avg: number | null }>; tagged?: boolean }) {
  const { t } = useAuth();
  const n = checksAllNote(parts);
  if (!n) return null;
  const vars = { count: n.count, avg: n.avg == null ? "—" : `${fmt(n.avg)} грн` };
  return (
    <div className="text-[10px] text-slate-500 mt-2 tabular-nums">
      {tagged && <KpiTag inKpi={false} />}
      {t(tagged ? "checks_all_note_plain" : "checks_all_note", vars)}
    </div>
  );
}

/** Метка «в KPI» / «не в KPI» перед строкой пояснения. */
function KpiTag({ inKpi }: { inKpi: boolean }) {
  const { t } = useAuth();
  const cls = inKpi ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-slate-200 text-slate-600 border border-slate-200";
  return <span className={`inline-block text-[9px] font-semibold px-1.5 rounded-full mr-1 align-[1px] ${cls}`}>{t(inKpi ? "tag_in_kpi" : "tag_not_in_kpi")}</span>;
}

function CriterionCard({ v }: { v: CriterionView }) {
  const { t } = useAuth();
  if (v.info) {
    return (
      <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs text-slate-500 uppercase tracking-wide">{v.label}</div>
            <div className="text-xl font-bold text-slate-700 mt-0.5 tabular-nums">{v.fact}</div>
            <div className="text-xs text-slate-500">{v.plan}</div>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 shrink-0">{t("crit_info_badge")}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-200">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 uppercase tracking-wide">{v.label}</div>
          <div className="text-xl font-bold text-slate-900 mt-0.5 tabular-nums">{v.fact}</div>
          {v.plan && <div className="text-xs text-slate-500">{v.plan}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm text-slate-500 tabular-nums">{t("crit_kpi")} <span className="font-semibold text-slate-900">{v.kpiText}</span></div>
          {v.sub && <div className="text-[10px] text-slate-500 tabular-nums">{v.sub}</div>}
        </div>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${TONE_BG[v.tone]}`} style={{ width: `${v.ratio * 100}%` }} />
      </div>
    </div>
  );
}
